import { StationTimingDeltaToneEnum } from "./station-timing-preview.models";

/**
 * The measured-times table puts every measurement of a leg side by side in one cell, so the 600px
 * confirm-dialog default clips them. Capped in viewport units as well, so a laptop still gets the
 * whole dialog on screen.
 */
export const STATION_TIMING_PREVIEW_MODAL_WIDTH = "min(1200px, 94vw)" as const;

/** Column count of the preview table; the expander row spans all of them. */
export const STATION_TIMING_PREVIEW_COLUMNS_COUNT = 5;

/**
 * What only a measured leg can fill: the measurements to pick from and their comparison against the
 * plan. A leg below the threshold replaces both with one note and still reports its sample size.
 */
export const STATION_TIMING_MEASURED_COLUMNS_COUNT = 2;

/**
 * Below this gap a measurement is treated as matching the plan rather than as an improvement or a
 * regression: GPS-derived leg times are not accurate to the second, so a smaller difference is noise.
 */
export const STATION_TIMING_ON_PLAN_TOLERANCE_SECONDS = 30;

/**
 * Direction glyph of a "vs planned" pill. Vertical arrows on purpose: they mean the same thing in an
 * RTL layout, unlike the horizontal arrow between two station names.
 */
export const STATION_TIMING_DELTA_ARROWS: Record<
  StationTimingDeltaToneEnum,
  string
> = {
  [StationTimingDeltaToneEnum.Slower]: "▲",
  [StationTimingDeltaToneEnum.Faster]: "▼",
  [StationTimingDeltaToneEnum.OnPlan]: "≈",
  [StationTimingDeltaToneEnum.NoPlan]: "",
};

/** Tone modifier of a "vs planned" pill; the styled wrapper owns what each one looks like. */
export const STATION_TIMING_DELTA_TONE_CLASSES: Record<
  StationTimingDeltaToneEnum,
  string
> = {
  [StationTimingDeltaToneEnum.Slower]: "station-timing__delta--slower",
  [StationTimingDeltaToneEnum.Faster]: "station-timing__delta--faster",
  [StationTimingDeltaToneEnum.OnPlan]: "station-timing__delta--on-plan",
  [StationTimingDeltaToneEnum.NoPlan]: "station-timing__delta--no-plan",
};

/** Ties the expander button to the row group it shows and hides. */
export const STATION_TIMING_NO_DATA_GROUP_ID = "station-timing-no-data-legs";

/** Legs are numbered from one for the user, while the row model indexes them from zero. */
export const STATION_TIMING_FIRST_LEG_NUMBER = 1;

/** Prefix of the per-leg day-of-week row group id; the leg index is appended to it. */
export const STATION_TIMING_DAY_GROUP_ID_PREFIX = "station-timing-days-leg";

/**
 * Translation keys of the weekday names already used by the shift-cycle editor, indexed the way the
 * backend buckets days: 0 = Sunday. Reused instead of a second weekday set.
 */
export const STATION_TIMING_DAY_NAME_KEYS = [
  "general.calendar.SUN",
  "general.calendar.MON",
  "general.calendar.TUE",
  "general.calendar.WED",
  "general.calendar.THU",
  "general.calendar.FRI",
  "general.calendar.SAT",
] as const;
